/**
 * LoadBot — BigLoads site-wide chat assistant widget.
 *
 * A self-contained floating chat bubble that:
 *  - Answers BigLoads questions (renting, listing, pricing, Haul Star, etc.)
 *    by POSTing the conversation to /api/chat (server calls Grok).
 *  - Persists a sessionId in localStorage so a returning visitor keeps one
 *    conversation thread (better data grouping server-side).
 *  - Reports the current page/path with every turn (intent-by-page analysis).
 *
 * Client-only: mounted inside ClientApp (not ServerApp) so it never touches SSR
 * output or SEO. No external dependencies — plain React + inline styles so it
 * can't collide with the marketplace CSS.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const STORAGE_KEY = 'bigloads_loadbot_session';
const HISTORY_KEY = 'bigloads_loadbot_history';
const REP_KEY = 'bigloads_loadbot_rep';

// Rotating support-rep names. One is chosen per visitor (persisted) so a
// returning visitor keeps talking to the same "person" instead of the name
// swapping mid-conversation.
const REP_NAMES = ['Carl', 'Joy', 'Beth', 'Dan'];

const getRepName = () => {
  if (typeof window === 'undefined') return REP_NAMES[0];
  try {
    let name = window.localStorage.getItem(REP_KEY);
    if (!name || !REP_NAMES.includes(name)) {
      name = REP_NAMES[Math.floor(Math.random() * REP_NAMES.length)];
      window.localStorage.setItem(REP_KEY, name);
    }
    return name;
  } catch (e) {
    return REP_NAMES[0];
  }
};

const COLORS = {
  navy: '#0E1B2A',
  navy2: '#152435',
  blue: '#2F7FE0',
  blueDark: '#1E5FB0',
  text: '#EAF1F8',
  textDim: '#9FB3C8',
  botBubble: '#1B2C40',
  userBubble: '#2F7FE0',
  border: '#25384f',
};

const getSessionId = () => {
  if (typeof window === 'undefined') return null;
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        'bl-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch (e) {
    return 'bl-' + Date.now();
  }
};

const loadHistory = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const saveHistory = msgs => {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-30)));
  } catch (e) {
    /* ignore */
  }
};

// ── link rendering ───────────────────────────────────────────────────────────
// The bot is instructed to hand out plain relative paths (/signup, /s, /blog…)
// plus the occasional full URL or support email. Rendered as raw text those
// look like dead "half links" (Jordan flagged 2026-08-14). This turns them into
// real, clickable anchors styled to match the widget. Relative paths are kept
// relative on purpose so they resolve to whatever host the visitor is on
// (www / staging / test) — but as actual <a href> they're now clickable.
//
// Matches (in one pass, non-overlapping):
//  - full URLs:      https://… or http://…
//  - emails:         support@bigloads.com
//  - known relative paths: /s, /signup, /login, /subscription, /blog,
//                          /blog/<slug>, /l/<...>, /confirm  (path chars only)
const LINK_RE = new RegExp(
  [
    // full http(s) URL
    'https?:\\/\\/[^\\s<>()]+',
    // email
    '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}',
    // known relative site paths (keep this list in sync with the system prompt).
    // Longer names FIRST so e.g. "/signup" isn't truncated to "/s" by the
    // shorter alternative. The bare one-letter paths (/s, /l) require a word
    // boundary (not followed by another letter) unless they continue with a
    // slash sub-path, so "/signup" never matches the "/s" branch.
    '\\/(?:signup|login|subscription|confirm|blog)(?:\\/[A-Za-z0-9._~%\\-/]*)?',
    '\\/(?:s|l)(?:\\/[A-Za-z0-9._~%\\-/]*|(?![A-Za-z]))',
  ].join('|'),
  'g'
);

// Trim trailing punctuation that commonly follows a link in prose ("…/signup."
// or "(see /s)") so it isn't swallowed into the href.
const stripTrailing = tok => {
  const m = tok.match(/^(.*?)([.,;:!?)\]]+)$/);
  return m ? { core: m[1], trail: m[2] } : { core: tok, trail: '' };
};

const hrefFor = core => {
  if (/^https?:\/\//i.test(core)) return core;
  if (core.includes('@') && !core.startsWith('/')) return `mailto:${core}`;
  return core; // relative path — resolves against current origin
};

// Turn a plain-text message into an array of strings + <a> elements.
const renderWithLinks = text => {
  if (typeof text !== 'string' || !text) return text;
  const out = [];
  let last = 0;
  let key = 0;
  let match;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    if (start > last) out.push(text.slice(last, start));
    const { core, trail } = stripTrailing(raw);
    const href = hrefFor(core);
    out.push(
      <a
        key={`lnk-${key++}`}
        href={href}
        {...(/^https?:\/\//i.test(core) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        style={styles.link}
      >
        {core}
      </a>
    );
    if (trail) out.push(trail);
    last = start + raw.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : text;
};

const greetingFor = name =>
  `Hey — I'm ${name} with BigLoads. Need to rent a trailer, list one, or have a question about how it all works? Ask away.`;

const GREETING = {
  role: 'assistant',
  content: greetingFor('the team'),
};

const QUICK_PROMPTS = [
  'How do I rent a trailer?',
  'How do I list my trailer?',
  'What does Haul Star cost?',
];

const LoadBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [repName, setRepName] = useState(REP_NAMES[0]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const [portalEl, setPortalEl] = useState(null);

  // Create a dedicated, fully-owned portal container appended to <body>.
  //
  // We do NOT portal into document.body or #portal-root directly: both caused
  // a stray `Page_root`-classed wrapper (id="page", min-height:100vh) to appear
  // around the widget during hydration, which added a full blank viewport of
  // height below the footer (the "footer extends way too far down" bug). By
  // creating our own bare <div id="loadbot-portal"> we control it completely:
  // no inherited class, no min-height, zero layout footprint (its only child is
  // position:fixed).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let el = document.getElementById('loadbot-portal');
    let created = false;
    if (!el) {
      el = document.createElement('div');
      el.id = 'loadbot-portal';
      // Belt-and-suspenders: even if some global CSS ever targets this node,
      // keep it from contributing any block-flow height of its own.
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      el.style.width = '0';
      el.style.height = '0';
      el.style.zIndex = '2147483000';
      document.body.appendChild(el);
      created = true;
    }
    setPortalEl(el);
    return () => {
      if (created && el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  // Hydrate on client mount only (avoids any SSR mismatch).
  useEffect(() => {
    sessionIdRef.current = getSessionId();
    const name = getRepName();
    setRepName(name);
    const saved = loadHistory();
    if (saved && saved.length) {
      setMessages(saved);
    } else {
      // Fresh visitor: personalize the greeting with their assigned rep.
      setMessages([{ role: 'assistant', content: greetingFor(name) }]);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated && messages.length > 1) saveHistory(messages);
  }, [messages, hydrated]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = useCallback(
    async text => {
      const content = (text || '').trim();
      if (!content || sending) return;

      const nextMessages = [...messages, { role: 'user', content }];
      setMessages(nextMessages);
      setInput('');
      setSending(true);

      const page =
        typeof window !== 'undefined'
          ? window.location.pathname + (window.location.search || '')
          : '';

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: nextMessages.map(m => ({ role: m.role, content: m.content })),
            sessionId: sessionIdRef.current,
            page,
          }),
        });
        const data = await res.json().catch(() => ({}));
        const reply =
          (data && data.reply) ||
          "Sorry, I hit a snag. Try again, or email support@bigloads.com and a real person will help.";
        setMessages(m => [...m, { role: 'assistant', content: reply }]);
      } catch (e) {
        setMessages(m => [
          ...m,
          {
            role: 'assistant',
            content:
              "Sorry, I couldn't reach the server just now. Give it another shot, or email support@bigloads.com.",
          },
        ]);
      } finally {
        setSending(false);
      }
    },
    [messages, sending]
  );

  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  // Don't render anything on the server, or before our owned portal container
  // has been created on the client.
  if (typeof window === 'undefined' || !portalEl) return null;

  // Hide the chat bubble on the listing CREATE/EDIT wizard (2026-09-29). The
  // floating launcher overlaps the form's fields + the Next/Save button on
  // mobile, and someone actively filling out a listing doesn't need the support
  // bubble in the way. Routes: '/l/new' and the edit wizard
  // '/l/{slug}/{id}/{type}/{tab}' where type is new|draft|edit. We deliberately
  // do NOT match the public listing view ('/l/{slug}/{id}' or the SEO/view
  // variants) so the bubble still shows there.
  const currentPath = window.location.pathname || '';
  const isListingWizard =
    currentPath === '/l/new' ||
    /^\/l\/[^/]+\/[^/]+\/(new|draft|edit)\/[^/]+/.test(currentPath);
  if (isListingWizard) return null;

  // Hide the chat bubble on the owner PRESELL pages (2026-10-02). These pages
  // are ~88% Meta in-app-browser traffic, where the floating launcher + popup
  // render poorly and add friction. Usage data confirmed near-zero value:
  // 1 chat session across all presell pages in 68 days (a confused in-app
  // browser visitor who typed their phone number asking to be called).
  // Routes: '/list-your-trailer' and its A/B variants
  // '/list-your-trailer/{rambo|grok|fable}'.
  const isPresellPage = /^\/list-your-trailer(\/[^/]+)?\/?$/.test(currentPath);
  if (isPresellPage) return null;

  // Hide the chat bubble on The Workshop (trailer-accessories) pages (2026-10-09).
  // The Workshop is a browse-and-click-to-affiliate flow, not a support-heavy
  // flow, and the floating launcher overlaps the gear cards on mobile. It has
  // nothing to do with trailer accessories, so keep it off these routes.
  // Routes: '/trailer-accessories' + all sub-paths (type + product pages).
  const isWorkshopPage = /^\/trailer-accessories(\/.*)?$/.test(currentPath);
  if (isWorkshopPage) return null;

  return createPortal(
    <div style={styles.root}>
      {open && (
        <div style={styles.panel} role="dialog" aria-label="BigLoads chat assistant">
          <div style={styles.header}>
            <div style={styles.headerTitle}>
              <span style={styles.dot} />
              {repName} · BigLoads Support
            </div>
            <button
              style={styles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div style={styles.messages} ref={scrollRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  ...styles.row,
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    ...styles.bubble,
                    ...(m.role === 'user' ? styles.userBubble : styles.botBubble),
                  }}
                >
                  {m.role === 'assistant' ? renderWithLinks(m.content) : m.content}
                </div>
              </div>
            ))}

            {messages.length <= 1 && !sending && (
              <div style={styles.quickWrap}>
                {QUICK_PROMPTS.map(q => (
                  <button key={q} style={styles.quickBtn} onClick={() => send(q)}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            {sending && (
              <div style={{ ...styles.row, justifyContent: 'flex-start' }}>
                <div style={{ ...styles.bubble, ...styles.botBubble, ...styles.typing }}>
                  <span style={styles.typingDot} />
                  <span style={{ ...styles.typingDot, animationDelay: '0.2s' }} />
                  <span style={{ ...styles.typingDot, animationDelay: '0.4s' }} />
                </div>
              </div>
            )}
          </div>

          <div style={styles.inputBar}>
            <textarea
              ref={inputRef}
              style={styles.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about renting, listing, pricing…"
              rows={1}
            />
            <button
              style={{ ...styles.sendBtn, opacity: input.trim() && !sending ? 1 : 0.5 }}
              onClick={() => send(input)}
              disabled={!input.trim() || sending}
              aria-label="Send message"
            >
              Send
            </button>
          </div>
          <div style={styles.footer}>Powered by BigLoads · we connect renters &amp; owners</div>
        </div>
      )}

      <button
        className="loadbot-launcher"
        style={{
          ...styles.launcher,
          ...(open ? { minWidth: '56px', width: '56px', padding: 0 } : {}),
        }}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? '×' : <span style={styles.launcherInner}>Any Questions?</span>}
      </button>

      <style>{keyframes}</style>
    </div>,
    portalEl
  );
};

const keyframes = `
@keyframes loadbotPop { from { transform: translateY(12px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
@keyframes loadbotBlink { 0%, 60%, 100% { opacity: 0.3; } 30% { opacity: 1; } }
/* On mobile the listing page shows a sticky Call/Text/Message bar pinned to the
   bottom (~66px + safe-area). When that bar is present it sets
   body.has-sticky-contact-bar; lift the chat launcher above it so it never
   covers the bar. Scoped to that body class so other pages are unaffected.
   Inline styles can't hold a media query, so this className rule wins via
   specificity + !important on small screens only. */
@media (max-width: 1023px) {
  body.has-sticky-contact-bar .loadbot-launcher { bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important; }
}
/* The subscription page has a taller two-line sticky CTA bar; lift the launcher
   a bit higher so the chat bubble never covers the START FREE MONTH button. */
@media (max-width: 1023px) {
  body.has-sub-sticky-bar .loadbot-launcher { bottom: calc(118px + env(safe-area-inset-bottom, 0px)) !important; }
}
`;

const styles = {
  root: {
    position: 'fixed',
    /* 2026-08-08: moved to bottom-LEFT (was bottom-right). On the search page
       the desktop Google Map lives on the right, and the bubble was covering
       it; left keeps it clear of the map + the map's bottom-right
       attribution/Terms strip and zoom controls. */
    left: '20px',
    bottom: '46px',
    zIndex: 2147483000,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    pointerEvents: 'none',
  },
  launcher: {
    position: 'fixed',
    left: '20px',
    bottom: '46px',
    zIndex: 2147483001,
    minWidth: '56px',
    height: '56px',
    padding: '0 22px',
    borderRadius: '28px',
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.blueDark})`,
    color: '#fff',
    fontSize: '22px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    whiteSpace: 'nowrap',
  },
  launcherInner: { fontSize: '16px', letterSpacing: '0.2px', fontWeight: 700 },
  panel: {
    position: 'fixed',
    left: '20px',
    bottom: '118px',
    zIndex: 2147483001,
    pointerEvents: 'auto',
    width: 'min(380px, calc(100vw - 32px))',
    height: 'min(560px, calc(100vh - 120px))',
    background: COLORS.navy,
    borderRadius: '16px',
    boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
    border: `1px solid ${COLORS.border}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'loadbotPop 0.18s ease-out',
  },
  header: {
    background: COLORS.navy2,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${COLORS.border}`,
  },
  headerTitle: {
    color: COLORS.text,
    fontWeight: 700,
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    background: '#3ED598',
    boxShadow: '0 0 8px #3ED598',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: COLORS.textDim,
    fontSize: '24px',
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    width: '28px',
    height: '28px',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  row: { display: 'flex', width: '100%' },
  bubble: {
    maxWidth: '82%',
    padding: '10px 13px',
    borderRadius: '14px',
    fontSize: '14px',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  botBubble: {
    background: COLORS.botBubble,
    color: COLORS.text,
    borderBottomLeftRadius: '4px',
  },
  link: {
    color: '#6FB4FF',
    fontWeight: 700,
    textDecoration: 'underline',
    wordBreak: 'break-word',
  },
  userBubble: {
    background: COLORS.userBubble,
    color: '#fff',
    borderBottomRightRadius: '4px',
  },
  quickWrap: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' },
  quickBtn: {
    textAlign: 'left',
    background: 'transparent',
    border: `1px solid ${COLORS.blue}`,
    color: COLORS.text,
    padding: '9px 12px',
    borderRadius: '10px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  typing: { display: 'flex', gap: '4px', alignItems: 'center', padding: '13px' },
  typingDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: COLORS.textDim,
    animation: 'loadbotBlink 1.2s infinite',
  },
  inputBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px',
    borderTop: `1px solid ${COLORS.border}`,
    background: COLORS.navy2,
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    resize: 'none',
    maxHeight: '100px',
    background: COLORS.navy,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  sendBtn: {
    background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.blueDark})`,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  footer: {
    fontSize: '11px',
    color: COLORS.textDim,
    textAlign: 'center',
    padding: '8px',
    background: COLORS.navy2,
  },
};

export default LoadBot;
